import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Edit as EditIcon,
  NotificationsOff as DeclineIcon,
  NotificationsActive as UndeclineIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useClinicTimezone } from '../hooks/useClinicTimezone';
import { DueListRow, reminderService, stageLabel } from '../services/reminderService';
import { calendarDaysUntil, formatCalendarDate } from '../utils/dateUtils';

interface Props {
  rows: DueListRow[];
  disableSend: boolean;
  disableSendReason?: string;
  showBarn: boolean;
  onChanged: () => void;
  onError: (err: any, fallback: string) => void;
}

// Days from the clinic's today (DATE-KINDS §2.9), off the date part the label prints, one count for
// both colours. `isPast(new Date(dueDate))` went red at 8 PM Eastern the evening before.
const dueDateColor = (row: DueListRow, clinicZone: string): 'error' | 'warning' | 'default' => {
  if (row.status !== 'PENDING' && row.status !== 'OVERDUE') return 'default';
  const daysLeft = calendarDaysUntil(row.dueDate, clinicZone);
  if (row.status === 'OVERDUE' || (daysLeft !== null && daysLeft < 0)) return 'error';
  return daysLeft !== null && daysLeft <= 14 ? 'warning' : 'default';
};

const lastEmailLabel = (row: DueListRow): { label: string; color: 'default' | 'success' | 'error' } | null => {
  const n = row.lastNotice;
  if (!n) return null;
  if (n.status === 'sent') {
    const delivery = n.emailStatus === 'opened' ? 'opened' : n.emailStatus === 'bounced' ? 'bounced' : 'sent';
    return {
      label: `${stageLabel(n.stage)} · ${delivery}`,
      color: delivery === 'bounced' ? 'error' : delivery === 'opened' ? 'success' : 'default',
    };
  }
  if (n.status === 'failed') return { label: `${stageLabel(n.stage)} · failed`, color: 'error' };
  if (n.status === 'skipped' && n.skipReason === 'undeliverable') {
    return { label: 'email undeliverable — call instead', color: 'error' };
  }
  return null;
};

const DueListTable: React.FC<Props> = ({
  rows,
  disableSend,
  disableSendReason,
  showBarn,
  onChanged,
  onError,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const clinicZone = useClinicTimezone();
  const [editRow, setEditRow] = useState<DueListRow | null>(null);
  const [editDate, setEditDate] = useState('');

  const sendMutation = useMutation((id: string) => reminderService.sendNow(id), {
    onSuccess: (result) => {
      enqueueSnackbar(
        result.sent > 0
          ? `Reminder emailed to ${result.sent} recipient${result.sent > 1 ? 's' : ''}`
          : 'No recipients could be emailed (missing email or unsubscribed)',
        { variant: result.sent > 0 ? 'success' : 'warning' },
      );
      onChanged();
    },
    onError: (err: any) => onError(err, 'Failed to send reminder'),
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Parameters<typeof reminderService.update>[1] }) =>
      reminderService.update(id, data),
    {
      onSuccess: () => onChanged(),
      onError: (err: any) => onError(err, 'Failed to update reminder'),
    },
  );

  const saveEditDate = () => {
    if (editRow && editDate) {
      updateMutation.mutate({ id: editRow.id, data: { dueDate: editDate } });
    }
    setEditRow(null);
  };

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Horse</TableCell>
            {showBarn && <TableCell>Barn</TableCell>}
            <TableCell>Item</TableCell>
            <TableCell>Due</TableCell>
            <TableCell>Recipients</TableCell>
            <TableCell>Last email</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const declined = !!row.declinedAt;
            const open = row.status === 'PENDING' || row.status === 'OVERDUE';
            const email = lastEmailLabel(row);
            return (
              <TableRow key={row.id} sx={declined ? { opacity: 0.55 } : undefined}>
                <TableCell>{row.horse.name}</TableCell>
                {showBarn && <TableCell>{row.barn?.name ?? '—'}</TableCell>}
                <TableCell>
                  {row.title}
                  {row.type && <Chip size="small" label={row.type} sx={{ ml: 1 }} />}
                  {declined && <Chip size="small" color="default" label="declined" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>
                  {/* CALENDAR — `RecallReminder.dueDate`, stored at UTC midnight
                      (DATE-KINDS.md §1.1; burndown §2.2). `new Date(iso).toLocaleDateString()`
                      is the native spelling of the same previous-day trap as
                      `format(new Date(iso))`: it re-reads the stored instant in the VIEWER'S
                      zone, so this chip showed 12/30/2024 for a reminder due 12/31/2024 in
                      every US clinic. `'M/d/yyyy'` reproduces what `toLocaleDateString()`
                      rendered for en-US, from the date PART, which is zone-invariant. */}
                  <Chip
                    size="small"
                    color={dueDateColor(row, clinicZone)}
                    label={formatCalendarDate(row.dueDate, 'M/d/yyyy')}
                    data-testid={`due-list-due-date-${row.id}`}
                  />
                </TableCell>
                <TableCell>
                  {row.recipients.length === 0 ? (
                    <Tooltip title="No linked owner with an email — link an owner on the horse to enable emails">
                      <Chip size="small" color="warning" label="no recipients" />
                    </Tooltip>
                  ) : (
                    row.recipients.map((r) => (
                      <Tooltip
                        key={r.clientId}
                        title={
                          !r.email
                            ? `${r.name}: no email on file`
                            : !r.emailEnabled
                              ? `${r.name}: unsubscribed`
                              : (r.email as string)
                        }
                      >
                        <Chip
                          size="small"
                          label={r.name}
                          color={r.email && r.emailEnabled ? 'default' : 'warning'}
                          sx={{ mr: 0.5 }}
                        />
                      </Tooltip>
                    ))
                  )}
                </TableCell>
                <TableCell>
                  {email ? (
                    <Typography variant="caption" color={email.color === 'default' ? 'text.secondary' : `${email.color}.main`}>
                      {email.label}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {open && !declined && (
                    <Tooltip title={disableSendReason ?? 'Send the reminder email now'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={disableSend || sendMutation.isLoading}
                          onClick={() => sendMutation.mutate(row.id)}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {open && (
                    <Tooltip title="Mark completed">
                      <IconButton
                        size="small"
                        onClick={() => updateMutation.mutate({ id: row.id, data: { status: 'COMPLETED' } })}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {open && (
                    <Tooltip title={declined ? 'Resume client emails for this item' : 'Decline — keep visible, never email'}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateMutation.mutate({
                            id: row.id,
                            data: { declinedAt: declined ? null : new Date().toISOString() },
                          })
                        }
                      >
                        {declined ? <UndeclineIcon fontSize="small" /> : <DeclineIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {open && (
                    <Tooltip title="Edit due date">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditRow(row);
                          setEditDate(row.dueDate.slice(0, 10));
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit due date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            sx={{ mt: 1 }}
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            label={editRow ? `${editRow.horse.name} — ${editRow.title}` : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditDate} disabled={!editDate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DueListTable;
